import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import type React from 'react';
import { useState } from 'react';
import { apiClient } from '../../services/api';

interface ManualSharepointWizardProps {
  onSuccess: () => void;
}

const ManualSharepointWizard: React.FC<ManualSharepointWizardProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    tenantId: '',
    siteId: '',
    driveId: '',
    siteUrl: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/setSharepointCredentials', {
        ...formData,
        authMode: 'multi-tenant',
      });
      onSuccess();
    } catch (err: unknown) {
      const error = err as Error;
      setError(error.message || 'An error occurred while saving credentials.');
    } finally {
      setLoading(false);
    }
  };

  const displaySiteUrl =
    formData.siteUrl.trim() || 'https://<YOUR_TENANT>.sharepoint.com/sites/<TARGET_SITE_NAME>';

  return (
    <Box sx={{ mt: 2, textAlign: 'left' }}>
      <Typography variant="body2" paragraph>
        For strict security compliance, manually assign the permission using our audited PowerShell
        script.
      </Typography>

      <TextField
        name="siteUrl"
        label="Site URL"
        value={formData.siteUrl}
        onChange={handleChange}
        fullWidth
        sx={{ mb: 3 }}
        placeholder="https://yourtenant.sharepoint.com/sites/YourSite"
        helperText="Enter your SharePoint Site URL first to generate the script below."
      />

      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 3 }}>
        <Typography variant="body2" component="pre" sx={{ overflowX: 'auto', m: 0 }}>
          {`# 1. Install the official PnP PowerShell module (if not installed)
Install-Module PnP.PowerShell -Scope CurrentUser -Force -AllowClobber

# 2. BIDLOGIQ Multi-Tenant App ID (Do NOT change this value)
$BIDLOGIQAppId = "92b4632c-5dbe-4293-a763-2819e09be75b"
$SiteUrl = "${displaySiteUrl}"

# 3. Connect to your SharePoint Site (Interactive Login)
Connect-PnPOnline -Url $SiteUrl -Interactive

# 4. Grant BIDLOGIQ Write access specifically to this site
Grant-PnPAzureADAppSitePermission -AppId $BIDLOGIQAppId -DisplayName "BIDLOGIQ Secure Connector" -Permissions Write

Write-Host "Success! The BIDLOGIQ app is now authorized to access $SiteUrl"`}
        </Typography>
      </Box>

      <Typography variant="body2" paragraph>
        After running the script, please provide the resulting Site ID, Drive ID, and your Tenant ID
        below:
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          name="tenantId"
          label="Tenant ID"
          value={formData.tenantId}
          onChange={handleChange}
          required
        />
        <TextField
          name="siteId"
          label="Site ID (Format: hostname,siteCollectionId,webId)"
          value={formData.siteId}
          onChange={handleChange}
          required
        />
        <TextField
          name="driveId"
          label="Drive ID"
          value={formData.driveId}
          onChange={handleChange}
          required
        />
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !formData.tenantId || !formData.siteId || !formData.driveId}
          sx={{ alignSelf: 'flex-start' }}
        >
          {loading ? <CircularProgress size={24} /> : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
};

export default ManualSharepointWizard;
